import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { AlertCircle, CheckCircle2, Loader2 } from "@/shared/ui/icons";

import { Badge, Button, Carte, EnTeteDePanneau, Etiquette, Saisie } from "@/shared/ui";
import { api as cyranoApi, ApiError } from "@/core/api/client";
import type { CreatorOnboardingState } from "@/panels/creators/useCreatorOnboardingState";

/** Étape "Connexion Telegram".
 *
 * L'user colle un bot token et clique « Connecter le bot » →
 * `PUT /api/creators/{id}/telegram-integration` (getMe + persist,
 * `is_enabled` reste false jusqu'à l'activation finale).
 *
 * Refonte 2026-06-21 (cockpit MTProto removal) : la section « compte
 * MTProto » (Section 2 historique) est retirée. Cyrano backend a retiré
 * MTProto complet — plus de comptes Telethon à connecter. `isValid` =
 * uniquement bot connecté.
 */

export interface TelegramConnectStepHandle {
  save: () => Promise<boolean>;
  isValid: boolean;
  isBusy: boolean;
}

interface TelegramConnectStepProps {
  cyranoCreatorId: string;
  creatorSlug: string;
  /** Si déjà connecté, on affiche le bot username au lieu du form. */
  currentBotUsername: string | null;
  currentBotFirstName: string | null;
  /** Conservé pour rétro-compatibilité de l'API du wizard ; ignoré
   *  (l'onboarding MTProto n'existe plus). */
  mtprotoRequired?: number;
  onValidityChange: (handle: TelegramConnectStepHandle) => void;
  errorMessage: string | null;
}

export function TelegramConnectStep({
  cyranoCreatorId,
  currentBotUsername,
  currentBotFirstName,
  onValidityChange,
  errorMessage,
}: TelegramConnectStepProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // ── Bot token ──
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [verifyResult, setVerifyResult] = useState<
    | { ok: true; username: string; firstName: string | null }
    | { ok: false; message: string }
    | null
  >(null);

  const botConnected = Boolean(currentBotUsername);
  const showTokenInput = !botConnected || replacing;
  const tokenLooksValid = token.trim().length > 10;

  const isValid = botConnected;

  async function connectBot(): Promise<boolean> {
    if (!tokenLooksValid) return false;
    setBusy(true);
    setVerifyResult(null);
    try {
      const resp = await cyranoApi.put<{
        bot_token_masked: string | null;
        bot_username: string | null;
        bot_first_name: string | null;
      }>(`/api/creators/${cyranoCreatorId}/telegram-integration`, {
        bot_token: token.trim(),
      });
      setVerifyResult({
        ok: true,
        username: resp.bot_username ?? "",
        firstName: resp.bot_first_name,
      });
      const botChanged =
        (resp.bot_username ?? "") !== (currentBotUsername ?? "");
      // Optimistic : reflète le bot connecté immédiatement. Si le bot a
      // changé, le backend reset aussi le vault → on aligne le cache local.
      queryClient.setQueryData<CreatorOnboardingState | undefined>(
        ["creator-onboarding-state", cyranoCreatorId],
        (old) =>
          old
            ? {
                ...old,
                telegram: {
                  ...old.telegram,
                  bot_done: true,
                  bot_username: resp.bot_username,
                  bot_first_name: resp.bot_first_name,
                  done: true,
                },
                telegram_group: botChanged
                  ? {
                      ...old.telegram_group,
                      done: false,
                      vault_chat_id: null,
                      vault_chat_title: null,
                      vault_verified_at: null,
                      notification_chat_id: null,
                      notification_chat_title: null,
                      notification_enabled: false,
                    }
                  : old.telegram_group,
              }
            : old,
      );
      await queryClient.invalidateQueries({
        queryKey: ["creator-onboarding-state"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["creators", "connections"],
      });
      setToken("");
      setReplacing(false);
      return true;
    } catch (err) {
      const message =
        err instanceof ApiError
          ? typeof err.body === "object" && err.body && "detail" in err.body
            ? String((err.body as { detail: unknown }).detail)
            : err.message
          : err instanceof Error
            ? err.message
            : String(err);
      setVerifyResult({ ok: false, message });
      return false;
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    onValidityChange({
      isValid,
      isBusy: busy,
      save: async () => {
        if (!botConnected && tokenLooksValid) {
          const ok = await connectBot();
          if (!ok) return false;
        }
        return isValid;
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid, busy, botConnected, tokenLooksValid, token]);

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-6 p-4 sm:p-8">
      <header className="flex flex-col gap-1">
        <Etiquette>{t("shell.creatorOnboardingWizard.steps.telegram.short")}</Etiquette>
        <EnTeteDePanneau
          niveau="h3"
          titre={t("shell.creatorOnboardingWizard.telegram.heading")}
          sousTitre={t("shell.creatorOnboardingWizard.telegram.help")}
          className="border-b-0 p-0 [&_.sf-bandeau-titre]:whitespace-normal"
        />
      </header>

      {/* Section bot Telegram */}
      <section className="flex flex-col gap-4">
        <div className="flex items-center gap-2.5">
          <Badge ton={botConnected ? "vert" : "gris"} size="md">
            {botConnected ? <CheckCircle2 className="h-3 w-3" aria-hidden /> : "1"}
          </Badge>
          <Etiquette as="h4" className="text-fg">
            {t("shell.creatorOnboardingWizard.telegram.bot.title")}
          </Etiquette>
        </div>

        {!botConnected && (
          <Carte remplissage="lg">
            <ol className="ml-4 list-decimal space-y-1.5 text-[13px] leading-relaxed text-muted">
              <li>{t("shell.creatorOnboardingWizard.botToken.howTo.step1")}</li>
              <li>{t("shell.creatorOnboardingWizard.botToken.howTo.step2")}</li>
              <li>{t("shell.creatorOnboardingWizard.botToken.howTo.step3")}</li>
              <li>{t("shell.creatorOnboardingWizard.botToken.howTo.step4")}</li>
            </ol>
            <Button
              variant="accent"
              size="lg"
              href="https://t.me/BotFather"
              target="_blank"
              className="mt-4"
            >
              {t("shell.creatorOnboardingWizard.botToken.openBotFather")}
            </Button>
          </Carte>
        )}

        {botConnected && (
          <Carte remplissage="md">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0 text-[13px]">
                <p className="font-semibold text-fg">
                  {t("shell.creatorOnboardingWizard.botToken.connected.title")}
                </p>
                <p className="mt-1 font-mono text-muted">
                  @{currentBotUsername}
                  {currentBotFirstName ? ` · ${currentBotFirstName}` : ""}
                </p>
              </div>
              <Button
                variant="secondary"
                size="lg"
                onClick={() => {
                  setReplacing((r) => !r);
                  if (replacing) {
                    setToken("");
                    setVerifyResult(null);
                  }
                }}
              >
                {replacing
                  ? t("shell.creatorOnboardingWizard.botToken.connected.cancelReplace")
                  : t("shell.creatorOnboardingWizard.botToken.connected.replace")}
              </Button>
            </div>
          </Carte>
        )}

        {showTokenInput && (
          <div className="flex flex-col gap-3">
            <Saisie.ChampSecret
              etiquette={
                botConnected
                  ? t("shell.creatorOnboardingWizard.botToken.replaceLabel")
                  : t("shell.creatorOnboardingWizard.botToken.tokenLabel")
              }
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="7304140728:AA...xxx"
              disabled={busy}
              autoFocus={!botConnected}
              taille="lg"
              afficherLabel={t("shell.creatorOnboardingWizard.botToken.show")}
              masquerLabel={t("shell.creatorOnboardingWizard.botToken.hide")}
            />
            <Button
              variant="primary"
              size="lg"
              onClick={() => void connectBot()}
              disabled={!tokenLooksValid || busy}
              className="self-start"
              icon={busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : undefined}
            >
              {t("shell.creatorOnboardingWizard.telegram.bot.connect")}
            </Button>
          </div>
        )}

        {verifyResult?.ok && (
          <Carte remplissage="sm">
            <div className="flex items-start gap-2.5 text-[13px]">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-valide" aria-hidden />
              <div className="min-w-0">
                <p className="font-medium text-fg">
                  {t("shell.creatorOnboardingWizard.botToken.verifyOk")}
                </p>
                <p className="mt-0.5 font-mono text-muted">
                  @{verifyResult.username}
                  {verifyResult.firstName ? ` · ${verifyResult.firstName}` : ""}
                </p>
              </div>
            </div>
          </Carte>
        )}

        {((verifyResult && !verifyResult.ok ? verifyResult.message : null) ||
          errorMessage) && (
          <Carte remplissage="sm" role="alert">
            <div className="flex items-start gap-2.5 text-[13px] text-fg">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-erreur" aria-hidden />
              <span className="min-w-0">
                {(verifyResult && !verifyResult.ok ? verifyResult.message : null) ??
                  errorMessage}
              </span>
            </div>
          </Carte>
        )}
      </section>
    </div>
  );
}
